import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import cls from 'clsx';
import { Left as Prev, Right as Next } from '@linktivity/link-icons';

import { ROW, COL } from './constants';
import { useDateContext } from './context';
import { IDateTableProps, CellType } from './DatePicker.types';
import dateUtils from './utils';
import styles from './datepicker.module.css';

const DateTable: React.FC<IDateTableProps> = ({
  date,
  onSelectDate,
  isSelected
}) => {
  const { t } = useTranslation();
  const {
    currentDate,
    setCurrentDate,
    setView,
    disabledDate,
    dateOptions,
    preDateOptions
  } = useDateContext();
  const currentYear = useMemo(() => currentDate.getFullYear(), [currentDate]);
  const currentMonth = useMemo(() => currentDate.getMonth(), [currentDate]);

  const isInDateOptions = useCallback(
    (cellDate: Date) => {
      const allOptions = [...(dateOptions || []), ...(preDateOptions || [])];
      if (!allOptions?.length) return true; // If dateOptions is not provided, all dates are selectable by default
      return allOptions.some(
        option =>
          dateUtils.dateToString(option) === dateUtils.dateToString(cellDate)
      );
    },
    [dateOptions, preDateOptions]
  );

  const isPreDate = useCallback(
    (cellDate: Date) => {
      if (!preDateOptions?.length) return false;
      return preDateOptions.some(
        preDate =>
          dateUtils.dateToString(preDate) === dateUtils.dateToString(cellDate)
      );
    },
    [preDateOptions]
  );

  const getCellType = useCallback(
    (day: number) => {
      const cellDate = new Date(currentYear, currentMonth, day);
      let type: CellType = 'normal';
      if (isPreDate(cellDate)) {
        type = 'preDate';
      }
      if (dateUtils.isToday(cellDate)) {
        type = 'today';
      }

      if (
        isSelected &&
        dateUtils.dateToString(cellDate) === dateUtils.dateToString(date)
      ) {
        type = isPreDate(cellDate) ? 'preSelected' : 'selected';
      }

      return type;
    },
    [date, currentYear, currentMonth, isSelected, isPreDate]
  );

  const tableCell = useMemo(() => {
    const month = dateUtils.getMonthArray(currentYear, currentMonth);
    const offset = dateUtils.getFirstDayOfMonth(currentDate);
    const cell = dateUtils.getDefaultTableCell();
    for (let j = offset; j < COL; j++) {
      const day = month.shift();
      if (day) {
        const cellDate = new Date(currentYear, currentMonth, day);
        cell[0][j].value = day.toString();
        cell[0][j].type = getCellType(day);
        // If dateOptions is provided, dates not in the list are disabled by default
        const isNotInOptions = !isInDateOptions(cellDate);
        const isDisabledByFunc = disabledDate?.(cellDate) || false;
        cell[0][j].disabled = isNotInOptions || isDisabledByFunc;
      }
    }
    for (let i = 1; i < ROW; i++) {
      for (let j = 0; j < COL; j++) {
        const day = month.shift();
        if (day) {
          const cellDate = new Date(currentYear, currentMonth, day);
          cell[i][j].value = day.toString();
          cell[i][j].type = getCellType(day);
          // If dateOptions is provided, dates not in the list are disabled by default
          const isNotInOptions = !isInDateOptions(cellDate);
          const isDisabledByFunc = disabledDate?.(cellDate) || false;
          cell[i][j].disabled = isNotInOptions || isDisabledByFunc;
        }
      }
    }
    // remove last empty row
    if (cell[ROW - 1].every(cell => !cell.value)) {
      delete cell[ROW - 1];
    }
    return cell;
  }, [
    currentYear,
    currentMonth,
    currentDate,
    getCellType,
    disabledDate,
    isInDateOptions
  ]);

  const getCellTitle = useCallback(
    (day: number) => {
      const cellDate = new Date(currentYear, currentMonth, day);
      return dateUtils.dateToString(cellDate);
    },
    [currentYear, currentMonth]
  );

  const prevMonth = useCallback(() => {
    setCurrentDate(dateUtils.prevMonth(currentYear, currentMonth));
  }, [currentYear, currentMonth, setCurrentDate]);

  const nextMonth = useCallback(() => {
    setCurrentDate(dateUtils.nextMonth(currentYear, currentMonth));
  }, [currentYear, currentMonth, setCurrentDate]);

  const prevYear = useCallback(() => {
    const prev = new Date(currentYear - 1, currentMonth, 1);
    setCurrentDate(prev);
  }, [currentYear, currentMonth, setCurrentDate]);

  const nextYear = useCallback(() => {
    const next = new Date(currentYear + 1, currentMonth, 1);
    setCurrentDate(next);
  }, [currentYear, currentMonth, setCurrentDate]);

  const handleClick = useCallback(
    (day: number) => {
      const cellDate = new Date(currentYear, currentMonth, day);
      onSelectDate(cellDate);
    },
    [currentYear, currentMonth, onSelectDate]
  );

  const showYearTable = useCallback(() => {
    setView('year');
  }, [setView]);

  const showMonthTable = useCallback(() => {
    setView('month');
  }, [setView]);

  return (
    <div className={styles.dateTable}>
      <div className={styles.dateTableHeader}>
        <div className={styles.dateTableWrapper}>
          <button
            type="button"
            className={styles.dateTableButton}
            onClick={prevYear}
          >
            <Prev className={styles.dateTableIcon} />
          </button>
          <button
            type="button"
            className={styles.dateTableCurrent}
            onClick={showYearTable}
          >
            {currentYear}
          </button>
          <button
            type="button"
            className={styles.dateTableButton}
            onClick={nextYear}
          >
            <Next className={styles.dateTableIcon} />
          </button>
        </div>
        <div className={styles.dateTableWrapper}>
          <button
            type="button"
            className={styles.dateTableButton}
            onClick={prevMonth}
          >
            <Prev className={styles.dateTableIcon} />
          </button>
          <button
            type="button"
            className={styles.dateTableCurrent}
            onClick={showMonthTable}
          >
            {t(`date.month.month${currentMonth + 1}`)}
          </button>
          <button
            type="button"
            className={styles.dateTableButton}
            onClick={nextMonth}
          >
            <Next className={styles.dateTableIcon} />
          </button>
        </div>
      </div>
      <div className={styles.dateTableBody} role="grid">
        <div className={styles.dateTableWeek}>
          <div className={styles.dateTableWeekItem}>{t('date.weeks.sun')}</div>
          <div className={styles.dateTableWeekItem}>{t('date.weeks.mon')}</div>
          <div className={styles.dateTableWeekItem}>{t('date.weeks.tue')}</div>
          <div className={styles.dateTableWeekItem}>{t('date.weeks.wed')}</div>
          <div className={styles.dateTableWeekItem}>{t('date.weeks.thu')}</div>
          <div className={styles.dateTableWeekItem}>{t('date.weeks.fri')}</div>
          <div className={styles.dateTableWeekItem}>{t('date.weeks.sat')}</div>
        </div>
        <div className={styles.dateTableMonth}>
          {tableCell.flat().map((cell, index) => (
            <div className={styles.dateTableCell} key={index}>
              {cell.value && (
                <button
                  type="button"
                  className={cls(styles.dateTableCellButton, {
                    [styles.today]: cell.type === 'today',
                    [styles.selected]: cell.type === 'selected',
                    [styles.preDate]: cell.type === 'preDate',
                    [styles.preSelected]: cell.type === 'preSelected'
                  })}
                  disabled={cell.disabled}
                  onClick={() => handleClick(Number(cell.value))}
                  title={getCellTitle(Number(cell.value))}
                >
                  {cell.value}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DateTable;
